import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertOctagon, Clock, TrendingUp, Users, CalendarClock, Building2, Sparkles } from "lucide-react";

const BADGE = "text-[10px] uppercase tracking-widest border rounded px-2 py-0.5 font-semibold";

function Sparkline({ values = [], stroke = "#059669", height = 24, width = 90 }) {
  if (!values.length) return <div style={{ height }} className="text-[9px] text-slate-300">no trend</div>;
  const max = Math.max(1, ...values);
  const min = Math.min(0, ...values);
  const step = width / Math.max(1, values.length - 1);
  const y = (v) => height - 2 - ((v - min) / (max - min || 1)) * (height - 6);
  const points = values.map((v, i) => `${i * step},${y(v)}`).join(" ");
  const last = values[values.length - 1];
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="mt-1">
      <polyline fill="none" stroke={stroke} strokeWidth="1.5" points={points} />
      <circle cx={(values.length - 1) * step} cy={y(last)} r="2" fill={stroke} />
    </svg>
  );
}

function Kpi({ label, value, tone, testid, trend, trendStroke }) {
  const tint = {
    ok: "border-l-emerald-500", warn: "border-l-amber-500",
    danger: "border-l-red-500", info: "border-l-sky-500",
  }[tone] || "border-l-slate-400";
  return (
    <div className={`bg-white border border-slate-200 border-l-4 ${tint} rounded-md p-4`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="flex items-end justify-between gap-2">
        <div className="font-heading text-2xl font-semibold text-slate-900 mt-1 tabular-nums">{value ?? 0}</div>
        <Sparkline values={trend || []} stroke={trendStroke || "#059669"} />
      </div>
    </div>
  );
}

export default function MigrationExecutive() {
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get("/migrations/executive/overview"),
      api.get("/migrations/executive/trends"),
    ]).then(([o, t]) => { setData(o.data); setTrends(t.data); })
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, []);

  if (err) return <div className="p-6 text-sm text-red-600" data-testid="exec-error">{err}</div>;
  if (!data) return <div className="p-6 text-sm text-slate-400" data-testid="exec-loading">Loading executive overview…</div>;

  const k = data.kpis || {};
  const s = trends?.series || {};
  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="page-migration-executive">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
          <TrendingUp className="w-3.5 h-3.5" /> Platform administration
        </div>
        <h1 className="font-heading text-3xl font-semibold text-slate-900 mt-1">Migrations · Executive Overview</h1>
        <p className="text-sm text-slate-500 mt-1">Cross-tenant view of every migration on the AYOBELL platform. Access is restricted to platform administrators.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="exec-kpis">
        <Kpi label="Tenants onboarding" value={k.tenants_onboarding} tone="info" testid="exec-kpi-tenants" trend={s.tenants_onboarding} trendStroke="#0284c7" />
        <Kpi label="In progress" value={k.in_progress} tone="info" testid="exec-kpi-inprogress" trend={s.in_progress} trendStroke="#0284c7" />
        <Kpi label="Awaiting approval" value={k.awaiting_approval} tone="warn" testid="exec-kpi-approval" trend={s.awaiting_approval} trendStroke="#d97706" />
        <Kpi label="Scheduled" value={k.scheduled} tone="info" testid="exec-kpi-scheduled" trend={s.scheduled} trendStroke="#0284c7" />
        <Kpi label="At-risk" value={k.at_risk} tone="danger" testid="exec-kpi-atrisk" trend={s.at_risk} trendStroke="#dc2626" />
        <Kpi label="Completed" value={k.completed} tone="ok" testid="exec-kpi-completed" trend={s.completed} trendStroke="#059669" />
      </div>
      {trends?.labels && (
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-4" data-testid="trend-labels">
          Trend windows: {trends.labels.join(" · ")}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-white border border-slate-200 rounded-md" data-testid="exec-at-risk">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold flex items-center gap-1.5">
              <AlertOctagon className="w-3.5 h-3.5" /> At-risk migrations
            </div>
            <span className="text-[10px] text-slate-400">{(data.at_risk || []).length} entries</span>
          </div>
          <div className="p-4">
            {(data.at_risk || []).length === 0
              ? <div className="text-sm text-slate-500" data-testid="exec-atrisk-empty">No at-risk migrations right now. 🎉</div>
              : (
                <ul className="space-y-2">
                  {data.at_risk.map((p) => (
                    <li key={p.id} className="border border-red-200 bg-red-50 rounded-md p-3" data-testid={`exec-atrisk-${p.ref}`}>
                      <div className="flex items-center justify-between">
                        <Link to={`/migration/${p.id}`} className="font-medium text-slate-900 hover:text-emerald-700">{p.ref} · {p.org_name}</Link>
                        <span className={`${BADGE} border-red-300 bg-red-100 text-red-800`}>{(p.status || "").replace(/_/g, " ")}</span>
                      </div>
                      <div className="text-xs text-red-800 mt-1">
                        {(p.risk_reasons || []).map((r) => <span key={r} className="inline-block mr-2">• {r}</span>)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-md" data-testid="exec-upcoming">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold flex items-center gap-1.5">
              <CalendarClock className="w-3.5 h-3.5" /> Upcoming go-lives
            </div>
            <span className="text-[10px] text-slate-400">{(data.upcoming_go_lives || []).length} entries</span>
          </div>
          <div className="p-4">
            {(data.upcoming_go_lives || []).length === 0
              ? <div className="text-sm text-slate-500" data-testid="exec-upcoming-empty">No go-lives scheduled in the next 45 days.</div>
              : (
                <ul className="space-y-2">
                  {data.upcoming_go_lives.map((p) => (
                    <li key={p.id} className="border border-slate-200 rounded-md p-3 flex items-center justify-between" data-testid={`exec-upcoming-${p.ref}`}>
                      <div>
                        <Link to={`/migration/${p.id}`} className="font-medium text-slate-900 hover:text-emerald-700">{p.ref} · {p.org_name}</Link>
                        <div className="text-[11px] text-slate-500">Go-live {p.go_live_date}</div>
                      </div>
                      <span className={`${BADGE} ${p.days_to_go_live < 0 ? "border-red-300 bg-red-50 text-red-800" : p.days_to_go_live <= 7 ? "border-amber-300 bg-amber-50 text-amber-800" : "border-emerald-300 bg-emerald-50 text-emerald-800"}`}>
                        {p.days_to_go_live < 0 ? `${Math.abs(p.days_to_go_live)}d overdue` : `${p.days_to_go_live}d`}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </section>
      </div>

      <section className="bg-white border border-slate-200 rounded-md mt-6" data-testid="exec-recent">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-1.5">
          <div className="text-[10px] uppercase tracking-widest text-slate-600 font-semibold flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Recent activity
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
              <th className="px-3 py-2">Ref</th>
              <th className="px-3 py-2">Organisation</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Errors</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Updated</th>
            </tr>
          </thead>
          <tbody>
            {(data.recent || []).map((p) => {
              const s = p.stats || {};
              return (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`exec-recent-${p.ref}`}>
                  <td className="px-3 py-2"><Link to={`/migration/${p.id}`} className="text-emerald-700 hover:underline font-medium">{p.ref}</Link></td>
                  <td className="px-3 py-2">{p.org_name}</td>
                  <td className="px-3 py-2 text-slate-500">{p.current_system || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.total_records || 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-700">{s.errors || 0}</td>
                  <td className="px-3 py-2">
                    <span className={`${BADGE} border-slate-300 bg-slate-100 text-slate-800`}>{(p.status || "").replace(/_/g, " ")}</span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">{p.updated_at?.slice(0, 16).replace("T", " ")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
